import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import Typo from "typo-js";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff, Trash2, Printer, Search, X, Download, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const Card = ({ title, children }) => (
  <div className="bg-[#1B3352] border border-white/10 rounded-2xl p-6 mb-6">
    {title && <h2 className="text-white text-lg font-medium mb-5">{title}</h2>}
    {children}
  </div>
);

const emptyMedEntry = { name: "", strength: "", composition: "", remark: "", intake: "Before Food", morning: "", afternoon: "", night: "", duration: "" };

let spellCheckerPromise = null;
const getSpellChecker = () => {
  if (!spellCheckerPromise) {
    spellCheckerPromise = Promise.all([
      fetch("/dictionaries/en_US.aff").then((r) => r.text()),
      fetch("/dictionaries/en_US.dic").then((r) => r.text()),
    ]).then(([affData, dicData]) => new Typo("en_US", affData, dicData));
  }
  return spellCheckerPromise;
};

const SpellSuggestions = ({ text, onReplace }) => {
  const [dictionary, setDictionary] = useState(null);
  const [misspelled, setMisspelled] = useState([]);
  const [openWord, setOpenWord] = useState(null);

  useEffect(() => {
    getSpellChecker().then(setDictionary).catch(() => {});
  }, []);

  useEffect(() => {
    if (!dictionary || !text) { setMisspelled([]); return; }
    const timer = setTimeout(() => {
      const words = text.match(/[A-Za-z']+/g) || [];
      const seen = new Set();
      const bad = [];
      for (const w of words) {
        const key = w.toLowerCase();
        if (w.length < 3 || seen.has(key)) continue;
        seen.add(key);
        if (!dictionary.check(w)) {
          bad.push({ word: w, suggestions: dictionary.suggest(w).slice(0, 4) });
        }
      }
      setMisspelled(bad);
    }, 400);
    return () => clearTimeout(timer);
  }, [text, dictionary]);

  if (misspelled.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {misspelled.map((m) => (
        <div key={m.word} className="relative">
          <button type="button" onClick={() => setOpenWord(openWord === m.word ? null : m.word)}
            className="px-2 py-1 rounded-full text-[11px] bg-red-500/10 text-red-300 border border-red-500/30 underline decoration-wavy decoration-red-400">
            {m.word}
          </button>
          {openWord === m.word && m.suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 bg-[#14263D] border border-white/15 rounded-lg overflow-hidden whitespace-nowrap">
              {m.suggestions.map((s) => (
                <button key={s} type="button"
                  onClick={() => {
                    const re = new RegExp(`\\b${m.word}\\b`, "g");
                    onReplace(text.replace(re, s));
                    setOpenWord(null);
                  }}
                  className="block w-full text-left px-3 py-1.5 text-xs text-white/80 hover:bg-white/10">
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const INVESTIGATION_OPTIONS = [
  "MRI Brain",
  "MRI Brain with contrast",
  "MR Angiogram Brain",
  "MR Venogram Brain",
  "CT Brain (with Bony cuts/ 3D face)",
  "CT Brain with contrast",
  "MRI Cervical Spine with screening whole spine",
  "MRI Dorsal Spine with screening whole spine",
  "MRI LS Spine with screening whole spine",
  "MRI Spine with contrast (Cervical/ Dorsal/Lumbosacral)",
  "BMD DEXA Scan Hip and Spine",
  "NCV Both UL/ Both LL/ all 4 limbs",
  "X Ray Cervical Spine AP and Lateral",
  "X Ray LS Spine AP and Lateral in Flexion extension",
  "X ray DL spine AP and Lateral",
  "CBC/RFT/LFT/PT/INR/aPTT/GRBS",
  "Serology (Triple) /Blood Group",
  "FBS/PPBS/Hba1c/ Thyroid Profile",
  "CXR/ECG/ECHO",
  "Lipid Profile/ Serum Vit D/Serum Vit B12",
];

const PHYSIOTHERAPY_OPTIONS = [
  "Back strengthening Exercises 20 min + 10 Min/day",
  "Neck Exercises (ROM) 5 min x 3 times/ day",
  "Isometric Neck exercises 5 min x times/ day",
  "Shoulder Exercises 10 min x 3 times/ day",
  "Quadriceps/ Knee exercises/ Static cycling",
];

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
};

const TodaysPatients = ({ token, onSelect, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [attendedIds, setAttendedIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    Promise.all([
      axios.get(`${API}/patients`, authHeader(token)),
      axios.get(`${API}/consultations`, authHeader(token)),
    ]).then(([pRes, cRes]) => {
      setItems(pRes.data.filter((p) => p.date === today));
      setAttendedIds(new Set(cRes.data.filter((c) => c.date === today).map((c) => c.patient_id)));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [token, refreshKey]);

  if (loaded && items.length === 0) return null;

  const waiting = items.filter((p) => !attendedIds.has(p.patient_id));
  const attended = items.filter((p) => attendedIds.has(p.patient_id));

  const PatientRow = ({ p, isAttended }) => (
    <button key={p.patient_id} onClick={() => onSelect(p)}
      data-testid={`clinic-todays-patient-${p.patient_id}`}
      className={`w-full text-left border rounded-lg p-3 flex items-center justify-between ${
        isAttended
          ? "bg-[#14263D]/50 border-white/5 hover:border-white/20 opacity-70"
          : "bg-[#14263D] border-white/10 hover:border-[#5ECCA0]/50"
      }`}>
      <div>
        <span className="text-white font-medium">{p.name}</span>
        <span className="text-white/50 text-sm ml-2">#{p.patient_id} · {p.mobile}</span>
      </div>
      {isAttended
        ? <span className="text-[#5ECCA0] text-xs">Attended</span>
        : (p.follow_up && <span className="text-[#5ECCA0] text-xs">Follow up: {p.follow_up}</span>)}
    </button>
  );

  return (
    <Card title="Today's Patients">
      {!loaded && <p className="text-white/40 text-sm">Loading...</p>}
      {loaded && (
        <>
          {waiting.length > 0 && (
            <div className="mb-4">
              <div className="text-[#5ECCA0] text-xs uppercase font-mono-ui mb-2">Waiting</div>
              <div className="space-y-2">
                {waiting.map((p) => <PatientRow key={p.patient_id} p={p} isAttended={false} />)}
              </div>
            </div>
          )}
          {attended.length > 0 && (
            <div>
              <div className="text-white/40 text-xs uppercase font-mono-ui mb-2">Attended</div>
              <div className="space-y-2">
                {attended.map((p) => <PatientRow key={p.patient_id} p={p} isAttended={true} />)}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

const PatientSearch = ({ token, onSelect }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [allPatients, setAllPatients] = useState([]);

  useEffect(() => {
    axios.get(`${API}/patients`, authHeader(token)).then((res) => setAllPatients(res.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const q = query.trim().toLowerCase();
    setResults(allPatients.filter((p) =>
      p.name.toLowerCase().split(/\s+/).some((word) => word.length > 1 && word.startsWith(q)) || p.mobile.includes(q) || String(p.patient_id).includes(q)
    ).sort((a, b) => {
      const aFirst = a.name.toLowerCase().split(/\s+/)[0].startsWith(q) ? 0 : 1;
      const bFirst = b.name.toLowerCase().split(/\s+/)[0].startsWith(q) ? 0 : 1;
      return aFirst !== bFirst ? aFirst - bFirst : a.name.localeCompare(b.name);
    }).slice(0, 40));
  }, [query, allPatients]);

  return (
    <Card title="Find Patient">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, mobile, or patient ID"
          className="pl-9 bg-[#14263D] border-white/15 text-white" data-testid="clinic-patient-search-input" />
      </div>
      {results.length > 0 && (
        <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
          {results.map((p) => (
            <button key={p.patient_id} onClick={() => { onSelect(p); setQuery(""); setResults([]); }}
              data-testid={`clinic-patient-result-${p.patient_id}`}
              className="w-full text-left bg-[#14263D] border border-white/10 rounded-lg p-3 hover:border-[#5ECCA0]/50">
              <span className="text-white font-medium">{p.name}</span>
              <span className="text-white/50 text-sm ml-2">#{p.patient_id} · {p.mobile}</span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
};

const PatientDetailsPanel = ({ patient, onClear }) => (
  <Card>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-white text-lg font-medium">Patient Details</h2>
      <button onClick={onClear} className="text-white/40 hover:text-white" data-testid="clinic-clear-patient-btn">
        <X size={18} />
      </button>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      <div className="text-white/80"><span className="text-white/40">Name:</span> {patient.name}</div>
      <div className="text-white/80"><span className="text-white/40">Age:</span> {patient.age}</div>
      <div className="text-white/80"><span className="text-white/40">Sex:</span> {patient.sex}</div>
      <div className="text-white/80"><span className="text-white/40">Mobile:</span> {patient.mobile}</div>
      <div className="text-white/80 sm:col-span-2"><span className="text-white/40">Address:</span> {patient.address}</div>
      <div className="text-white/80"><span className="text-white/40">Referred By:</span> {patient.referred_by}</div>
      <div className="text-white/80"><span className="text-white/40">Date:</span> {patient.date}</div>
      <div className="text-white/80"><span className="text-white/40">Follow Up:</span> {patient.follow_up}</div>
      <div className="text-white/80"><span className="text-white/40">Pulse:</span> {patient.pulse}</div>
      <div className="text-white/80"><span className="text-white/40">BP:</span> {patient.blood_pressure}</div>
      <div className="text-white/80"><span className="text-white/40">Height:</span> {patient.height}</div>
      <div className="text-white/80"><span className="text-white/40">Weight:</span> {patient.weight}</div>
    </div>
  </Card>
);

const ConditionField = ({ label, value, onChange, testId }) => {
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    if (value === "" && (status !== "" || detail !== "")) {
      setStatus("");
      setDetail("");
    }
  }, [value]); // eslint-disable-line

  const apply = (newStatus, newDetail) => {
    setStatus(newStatus);
    setDetail(newDetail);
    onChange(newStatus ? (newDetail.trim() ? `${newStatus} - ${newDetail.trim()}` : newStatus) : "");
  };

  return (
    <div>
      <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">{label}</Label>
      <div className="flex gap-2 mt-2 mb-2">
        <button type="button" onClick={() => apply("Yes", detail)} data-testid={`${testId}-yes-btn`}
          className={`px-3 py-1 rounded-full text-xs font-medium ${status === "Yes" ? "bg-[#5ECCA0] text-[#14263D]" : "bg-[#14263D] text-white/60 border border-white/15"}`}>
          Yes
        </button>
        <button type="button" onClick={() => apply("No", detail)} data-testid={`${testId}-no-btn`}
          className={`px-3 py-1 rounded-full text-xs font-medium ${status === "No" ? "bg-[#5ECCA0] text-[#14263D]" : "bg-[#14263D] text-white/60 border border-white/15"}`}>
          No
        </button>
      </div>
      <Input value={detail} onChange={(e) => apply(status, e.target.value)} placeholder="Detail (optional)" maxLength={150} spellCheck="true"
        className="bg-[#14263D] border-white/15 text-white text-xs" data-testid={`${testId}-detail-input`} />
      <SpellSuggestions text={detail} onReplace={(v) => apply(status, v)} />
    </div>
  );
};

const ClinicalAssessmentCard = ({ diagnosis, setDiagnosis, dm, setDm, htn, setHtn, copd, setCopd, antiplatelets, setAntiplatelets, thyroid, setThyroid }) => (
  <Card title="Clinical Assessment">
    <div className="mb-5">
      <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Diagnosis</Label>
      <Input value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} spellCheck="true"
        className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="clinic-diagnosis-input" />
      <SpellSuggestions text={diagnosis} onReplace={setDiagnosis} />
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
      <ConditionField label="DM" value={dm} onChange={setDm} testId="clinic-dm" />
      <ConditionField label="HTN" value={htn} onChange={setHtn} testId="clinic-htn" />
      <ConditionField label="COPD" value={copd} onChange={setCopd} testId="clinic-copd" />
      <ConditionField label="Antiplatelets" value={antiplatelets} onChange={setAntiplatelets} testId="clinic-antiplatelets" />
      <ConditionField label="Thyroid" value={thyroid} onChange={setThyroid} testId="clinic-thyroid" />
    </div>
  </Card>
);

const ClinicalNotesInvestigationCard = ({ clinicalNotes, setClinicalNotes, investigations, setInvestigations }) => {
  const [selectedTests, setSelectedTests] = useState([]);
  const [investigationOther, setInvestigationOther] = useState("");

  useEffect(() => {
    if (investigations === "" && (selectedTests.length > 0 || investigationOther !== "")) {
      setSelectedTests([]);
      setInvestigationOther("");
    }
  }, [investigations]); // eslint-disable-line

  const updateInvestigations = (tests, other) => {
    const parts = [...tests];
    if (other.trim()) parts.push(other.trim());
    setInvestigations(parts.join("\n"));
  };

  const toggleTest = (t) => {
    const updated = selectedTests.includes(t) ? selectedTests.filter((x) => x !== t) : [...selectedTests, t];
    setSelectedTests(updated);
    updateInvestigations(updated, investigationOther);
  };

  const onOtherChange = (e) => {
    const val = e.target.value;
    setInvestigationOther(val);
    updateInvestigations(selectedTests, val);
  };

  const replaceInvestigationOther = (val) => {
    setInvestigationOther(val);
    updateInvestigations(selectedTests, val);
  };

  return (
    <Card title="Clinical Notes & Investigation">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui block mb-2">Clinical Notes</Label>
          <textarea value={clinicalNotes} onChange={(e) => setClinicalNotes(e.target.value)} rows={8} spellCheck="true"
            className="w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-3 text-sm"
            data-testid="clinic-clinical-notes-input" />
          <SpellSuggestions text={clinicalNotes} onReplace={setClinicalNotes} />
        </div>
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui block mb-2">Investigation</Label>
          <div className="flex flex-wrap gap-2 mb-3">
            {INVESTIGATION_OPTIONS.map((t, i) => (
              <button key={t} type="button" onClick={() => toggleTest(t)}
                data-testid={`clinic-investigation-toggle-${i}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${
                  selectedTests.includes(t) ? "bg-[#5ECCA0] text-[#14263D]" : "bg-[#14263D] text-white/60 border border-white/15"
                }`}>
                {t}
              </button>
            ))}
          </div>
          <textarea value={investigationOther} onChange={onOtherChange} rows={3} spellCheck="true"
            placeholder="Anything else (optional)"
            className="w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-3 text-sm"
            data-testid="clinic-investigation-other-input" />
          <SpellSuggestions text={investigationOther} onReplace={replaceInvestigationOther} />
        </div>
      </div>
    </Card>
  );
};

const PhysiotherapyCard = ({ physiotherapy, setPhysiotherapy }) => {
  const [selected, setSelected] = useState([]);
  const [other, setOther] = useState("");

  useEffect(() => {
    if (physiotherapy === "" && (selected.length > 0 || other !== "")) {
      setSelected([]);
      setOther("");
    }
  }, [physiotherapy]); // eslint-disable-line

  const update = (sel, oth) => {
    const parts = [...sel];
    if (oth.trim()) parts.push(oth.trim());
    setPhysiotherapy(parts.join("\n"));
  };

  const toggle = (t) => {
    const updated = selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t];
    setSelected(updated);
    update(updated, other);
  };

  const onOtherChange = (e) => {
    const val = e.target.value;
    setOther(val);
    update(selected, val);
  };

  const replaceOther = (val) => {
    setOther(val);
    update(selected, val);
  };

  return (
    <Card title="Physiotherapy">
      <div className="flex flex-wrap gap-2 mb-3">
        {PHYSIOTHERAPY_OPTIONS.map((t, i) => (
          <button key={t} type="button" onClick={() => toggle(t)}
            data-testid={`clinic-physio-toggle-${i}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${
              selected.includes(t) ? "bg-[#5ECCA0] text-[#14263D]" : "bg-[#14263D] text-white/60 border border-white/15"
            }`}>
            {t}
          </button>
        ))}
      </div>
      <textarea value={other} onChange={onOtherChange} rows={3} spellCheck="true"
        placeholder="Anything else (optional)"
        className="w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-3 text-sm"
        data-testid="clinic-physio-other-input" />
      <SpellSuggestions text={other} onReplace={replaceOther} />
    </Card>
  );
};

const MedicineForm = ({ token, editingEntry, onSave, onCancelEdit }) => {
  const [medicines, setMedicines] = useState([]);
  const [entry, setEntry] = useState(emptyMedEntry);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/medicines`, authHeader(token)).then((res) => setMedicines(res.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (editingEntry) {
      setEntry({ ...emptyMedEntry, ...editingEntry });
    }
  }, [editingEntry]);

  const set = (k) => (e) => setEntry((f) => ({ ...f, [k]: e.target.value }));

  const onNameChange = (e) => {
    const val = e.target.value;
    setEntry((f) => ({ ...f, name: val }));
    const q = val.trim().toLowerCase();
    if (q) {
      const matches = medicines
        .filter((m) => m.name.toLowerCase().split(/\s+/).some((word) => word.length > 1 && word.startsWith(q)))
        .sort((a, b) => a.name.localeCompare(b.name));
      setSuggestions(matches.slice(0, 20));
    } else {
      setSuggestions([]);
    }
  };

  const pickSuggestion = (m) => {
    const r = (m.remarks || "").toUpperCase();
    let intake = entry.intake;
    if (r.includes("AFTER FOOD")) intake = "After Food";
    else if (r.includes("EMPTY STOMACH")) intake = "Empty Stomach";
    else if (r.includes("BEFORE FOOD")) intake = "Before Food";
    setEntry((f) => ({ ...f, name: m.name, strength: m.strength, composition: m.composition || "", intake }));
    setSuggestions([]);
  };

  const handleSave = () => {
    if (!entry.name.trim()) { toast.error("Add a medicine name first"); return; }
    onSave(entry);
    setEntry(emptyMedEntry);
  };

  const handleCancel = () => {
    setEntry(emptyMedEntry);
    onCancelEdit();
  };

  return (
    <Card title={editingEntry ? "Edit Medicine" : "Medicine Details"}>
      <div className="grid sm:grid-cols-3 gap-5 mb-4">
        <div className="relative">
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Medicine Name</Label>
          <Input value={entry.name} onChange={onNameChange} placeholder="Start typing to search..."
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="clinic-medicine-name-input" />
          {suggestions.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-[#14263D] border border-white/15 rounded-lg max-h-64 overflow-y-auto">
              {suggestions.map((m) => (
                <button key={m.id} type="button" onClick={() => pickSuggestion(m)}
                  className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10">
                  {m.name} {m.strength && <span className="text-white/40">— {m.strength}</span>}
                  {m.remarks && <span className="text-white/30 text-xs block">{m.remarks}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Mg / Strength</Label>
          <Input value={entry.strength} onChange={set("strength")} placeholder="e.g. 300mg"
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="clinic-medicine-strength-input" />
        </div>
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Composition</Label>
          <Input value={entry.composition} onChange={set("composition")} placeholder="e.g. Gabapentin"
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="clinic-medicine-composition-input" />
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-5 mb-4">
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Intake</Label>
          <select value={entry.intake} onChange={set("intake")} data-testid="clinic-medicine-intake-select"
            className="mt-2 w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-2.5 text-sm">
            <option value="After Food">After Food</option>
            <option value="Before Food">Before Food</option>
            <option value="Empty Stomach">Empty Stomach</option>
          </select>
        </div>
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">B</Label>
          <Input value={entry.morning} onChange={set("morning")} placeholder="e.g. 1"
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="clinic-medicine-morning-input" />
        </div>
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">L</Label>
          <Input value={entry.afternoon} onChange={set("afternoon")} placeholder="e.g. 0"
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="clinic-medicine-afternoon-input" />
        </div>
        <div>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">D</Label>
          <Input value={entry.night} onChange={set("night")} placeholder="e.g. 1"
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="clinic-medicine-night-input" />
        </div>
      </div>

      <div className="mb-5">
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Add. Remark</Label>
        <Input value={entry.remark} onChange={set("remark")} placeholder="e.g. Weekly twice, Monthly once"
          className="mt-2 bg-[#14263D] border-white/15 text-white max-w-md" data-testid="clinic-medicine-remark-input" />
      </div>

      <div className="mb-5">
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Duration</Label>
        <Input value={entry.duration} onChange={set("duration")} placeholder="e.g. 5 days"
          className="mt-2 bg-[#14263D] border-white/15 text-white max-w-xs" data-testid="clinic-medicine-duration-input" />
      </div>

      <div className="flex items-center gap-3">
        <button type="button" onClick={handleSave} data-testid="clinic-add-to-list-btn"
          className="rounded-full bg-blue-500 text-white font-medium px-6 py-2.5">
          {editingEntry ? "Update Medicine" : "Add to List"}
        </button>
        {editingEntry && (
          <button type="button" onClick={handleCancel} className="text-white/50 hover:text-white text-sm">
            Cancel
          </button>
        )}
      </div>
    </Card>
  );
};

const MedicineListTable = ({ items, onRemove, onEdit }) => (
  <Card title="Medicine List">
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="text-[#5ECCA0] text-xs uppercase font-mono-ui border-b border-white/10">
            <th className="py-2 pr-4">Sl.</th>
            <th className="py-2 pr-4">Medicine</th>
            <th className="py-2 pr-4">Mg</th>
            <th className="py-2 pr-4">Intake</th>
            <th className="py-2 pr-4">Remark</th>
            <th className="py-2 pr-4">B</th>
            <th className="py-2 pr-4">L</th>
            <th className="py-2 pr-4">D</th>
            <th className="py-2 pr-4">Duration</th>
            <th className="py-2 pr-4">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m, i) => (
            <tr key={i} className="border-b border-white/5 text-white/80">
              <td className="py-2 pr-4">{i + 1}</td>
              <td className="py-2 pr-4">
                {m.name}
                {m.composition && <span className="block text-white/40 text-xs">{m.composition}</span>}
              </td>
              <td className="py-2 pr-4">{m.strength}</td>
              <td className="py-2 pr-4">{m.intake}</td>
              <td className="py-2 pr-4">{m.remark}</td>
              <td className="py-2 pr-4">{m.morning}</td>
              <td className="py-2 pr-4">{m.afternoon}</td>
              <td className="py-2 pr-4">{m.night}</td>
              <td className="py-2 pr-4">{m.duration}</td>
              <td className="py-2 pr-4">
                <div className="flex items-center gap-3">
                  <button onClick={() => onEdit(i)} className="text-[#5ECCA0] hover:text-white" title="Edit">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onRemove(i)} className="text-red-400 hover:underline" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={10} className="py-4 text-white/40">No medicines added yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);

const ConsultationHistory = ({ token, patientId, patient }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!patientId) return;
    axios.get(`${API}/consultations/${patientId}`, authHeader(token)).then((res) => setItems(res.data)).catch(() => {});
  }, [token, patientId]);

  const reprint = (c) => {
    printPrescription(patient, c.medicines || [], {
      diagnosis: c.diagnosis, dm: c.dm, htn: c.htn, copd: c.copd,
      antiplatelets: c.antiplatelets, thyroid: c.thyroid,
      clinical_notes: c.clinical_notes, investigations: c.investigations, physiotherapy: c.physiotherapy,
    }, c.additional_remarks, c.instructions);
  };

  return (
    <Card title="Consultation History">
      {items.length === 0 && <p className="text-white/40 text-sm">No consultation history.</p>}
      {items.map((c, i) => (
        <div key={c.id} className={`border-b border-white/10 py-3 last:border-0 ${i === 0 ? "bg-[#5ECCA0]/5 -mx-6 px-6 rounded-lg" : ""}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              {i === 0 && <div className="text-[#5ECCA0] text-xs uppercase font-mono-ui mb-1">Last Prescription</div>}
              <div className="text-white text-sm font-medium">{formatDateTime(c.created_at) || c.date}</div>
              {c.diagnosis && <div className="text-white/50 text-xs mt-0.5">Diagnosis: {c.diagnosis}</div>}
              <div className="text-white/60 text-sm mt-1">
                {(c.medicines || []).map((m) => m.name).join(", ") || "No medicines recorded"}
              </div>
              {c.additional_remarks && <div className="text-white/40 text-xs mt-1">Remarks: {c.additional_remarks}</div>}
            </div>
            <button onClick={() => reprint(c)} data-testid={`clinic-reprint-btn-${c.id}`}
              className="text-[#5ECCA0] hover:text-white flex-shrink-0" title="Print this prescription">
              <Printer size={16} />
            </button>
          </div>
        </div>
      ))}
    </Card>
  );
};

const formatPrintDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const suffix = (day % 10 === 1 && day !== 11) ? "st" :
                 (day % 10 === 2 && day !== 12) ? "nd" :
                 (day % 10 === 3 && day !== 13) ? "rd" : "th";
  return `${day}${suffix} ${month} ${year}`;
};

const buildPrescriptionHTML = (patient, medicines, clinical, remarks, instructions) => {
  const rows = medicines.map((m, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><span class="med-name">${m.name}</span>${m.composition ? `<span class="med-composition">${m.composition}</span>` : ""}</td>
      <td>${m.strength}</td>
      <td>${m.intake}</td>
      <td>${m.remark || ""}</td>
      <td>${m.morning || "\u2013"}</td>
      <td>${m.afternoon || "\u2013"}</td>
      <td>${m.night || "\u2013"}</td>
      <td>${m.duration}</td>
    </tr>`).join("");

  const bulletList = (text) => {
    if (!text) return "";
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return "";
    return `<ul style="margin:4px 0 0 18px; padding:0; font-size:12.5px;">${lines.map((l) => `<li style="margin-bottom:2px;">${l}</li>`).join("")}</ul>`;
  };

  return `
    <html>
      <head>
        <title>Prescription - ${patient.name}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #14263D; padding: 30px; margin: 0; box-sizing: border-box; min-height: 100vh; display: flex; flex-direction: column; overflow-wrap: break-word; word-break: break-word; }
          * { overflow-wrap: break-word; word-break: break-word; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; border-bottom: 2px solid #3E7B5E; padding-bottom: 12px; margin-bottom: 16px; }
          .header h1 { font-size: 19px; margin: 0 0 2px; font-weight: bold; }
          .cred { color: #14263D; font-size: 12px; margin: 1px 0; }
          .reg { font-size: 11px; margin-top: 5px; }
          .fellowships { font-size: 10px; color: #333; text-align: right; line-height: 1.3; max-width: 320px; }
          .fellowships .fname { display: block; color: #14263D; }
          .fellowships .floc { display: block; color: #666; margin-bottom: 3px; }
          .patient-name-line { font-size: 15px; margin-bottom: 6px; text-transform: uppercase; }
          .patient-name-line .label { color: #777; }
          .patient-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; font-size: 14px; margin-bottom: 8px; text-transform: uppercase; }
          .patient-grid span.label { color: #777; }
          .patient-grid-small { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 16px; font-size: 11.5px; margin-bottom: 8px; text-transform: uppercase; }
          .patient-grid-small span.label { color: #777; }
          .vitals-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; font-size: 12px; margin: 0; padding: 8px 0; border-top: 1px solid #ddd; }
          .vitals-row span.label { color: #777; display: block; font-size: 10px; text-transform: uppercase; }
          .diagnosis-row { font-size: 12px; padding: 8px 0 12px; border-top: 1px solid #ddd; border-bottom: 1px solid #ddd; margin-bottom: 12px; }
          .diagnosis-row .label { color: #777; text-transform: uppercase; font-size: 10px; margin-right: 6px; }
          .notes-investigations-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 10px; }
          .section-title { font-size: 11.5px; font-weight: bold; color: #14263D; text-transform: uppercase; text-decoration: underline; margin: 14px 0 4px; }
          .clinical-notes { font-size: 12.5px; margin-bottom: 4px; white-space: pre-line; }
          table.meds { border-collapse: collapse; width: 100%; margin-top: 8px; font-size: 12px; }
          table.meds th { background: #14263D; color: white; padding: 6px 8px; text-align: left; font-size: 11px; }
          table.meds td { padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
          .med-name { font-weight: 700; }
          .med-composition { display: block; font-size: 9px; color: #888; margin-top: 1px; }
          .physio-line { font-size: 12.5px; margin: 12px 0; font-style: italic; }
          .investigations { font-size: 12.5px; min-height: 18px; margin-bottom: 4px; white-space: pre-line; }
          .followup { margin-top: auto; padding-top: 20px; border: 1px solid #14263D; padding: 10px 14px; font-size: 11px; line-height: 1.6; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>Dr. Amit Kumar Chanduka</h1>
            <div class="cred">MS, M.Ch Neurosurgery (Gold Medalist)</div>
            <div class="cred">Brain &amp; Spine Surgeon &middot; Senior Consultant Neurosurgeon</div>
            <div class="reg">OCMR-15781/06</div>
          </div>
          <div class="fellowships">
            <span class="fname">Fellowship in Spine Surgery</span><span class="floc">(Sir GangaRam Hospital, New Delhi, India)</span>
            <span class="fname">Fellowship in Endoscopic Spine Surgery</span><span class="floc">(Seoul, South Korea)</span>
            <span class="fname">Fellowship in Endovascular Neurointervention</span><span class="floc">(SMS Hospital, Jaipur, India)</span>
            <span class="fname">Fellowship in Stereotactic &amp; Functional Neurosurgery</span><span class="floc">(Seoul, South Korea)</span>
            <span class="fname">Formerly at Manipal Hospital &amp; Sakra World Hospital, Bangalore</span>
          </div>
        </div>

        <div class="patient-name-line"><span class="label">Patient:</span> <b>${patient.name}</b>${(patient.age || patient.sex) ? ` (${patient.age}/${patient.sex})` : ""}</div>
        <div class="patient-grid">
          <div><span class="label">Mobile:</span> ${patient.mobile}</div>
          <div><span class="label">Date:</span> ${formatPrintDate(patient.date)}</div>
        </div>
        <div class="patient-grid-small" style="grid-template-columns: 2fr 1fr;">
          <div><span class="label">Address:</span> ${patient.address || "-"}</div>
          <div><span class="label">Referred By:</span> ${patient.referred_by || "-"}</div>
        </div>

        <div class="vitals-row">
          <div><span class="label">Pulse</span>${patient.pulse || "-"}</div>
          <div><span class="label">BP</span>${patient.blood_pressure || "-"}</div>
          <div><span class="label">Height</span>${patient.height || "-"}</div>
          <div><span class="label">Weight</span>${patient.weight || "-"}</div>
          <div><span class="label">BMI</span>${patient.bmi || "-"}</div>
        </div>

        <div class="vitals-row">
          <div><span class="label">DM</span>${clinical.dm || "-"}</div>
          <div><span class="label">HTN</span>${clinical.htn || "-"}</div>
          <div><span class="label">COPD</span>${clinical.copd || "-"}</div>
          <div><span class="label">Antiplatelets</span>${clinical.antiplatelets || "-"}</div>
          <div><span class="label">Thyroid</span>${clinical.thyroid || "-"}</div>
        </div>

        <div class="diagnosis-row"><span class="label">Diagnosis:</span> <b>${clinical.diagnosis || "-"}</b></div>

        ${(clinical.clinical_notes || clinical.investigations) ? `<div class="notes-investigations-row">
          <div>${clinical.clinical_notes ? `<div class="section-title">Clinical Notes</div>${bulletList(clinical.clinical_notes)}` : ""}</div>
          <div>${clinical.investigations ? `<div class="section-title">Investigations</div>${bulletList(clinical.investigations)}` : ""}</div>
        </div>` : ""}

        <table class="meds">
          <thead><tr><th>Sl.</th><th>Medicine Name</th><th>Mg</th><th>Intake</th><th>Remark</th><th>B</th><th>L</th><th>D</th><th>Duration</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>

        ${clinical.physiotherapy ? `<div class="section-title">Physiotherapy</div>${bulletList(clinical.physiotherapy)}` : ""}
        ${remarks ? `<div style="margin-top:12px;font-size:12.5px;"><b style="text-decoration:underline;">Remarks:</b>${bulletList(remarks)}</div>` : ""}
        ${instructions ? `<div style="margin-top:8px;font-size:12.5px;"><b style="text-decoration:underline;">Instructions:</b>${bulletList(instructions)}</div>` : ""}

        <div class="followup">
          <b>Follow Up:</b> ___________________&nbsp;&nbsp;&nbsp;
          <b>Appointments:</b> 9040965647 / 9583021000 / 9938122302<br>
          <b>Life &amp; Mind Clinic:</b> Plot no. 1428, Unit-17, Sastrinagar, Nayapalli, Bhubaneswar<br>
          <b>KIMS Superspeciality Hospital:</b> Kiit Campus-5, Kiit University, Patia, Bhubaneswar&nbsp;&nbsp;&nbsp;
          <b>Tel:</b> +91 674 6661016 / 6661066
        </div>
      </body>
    </html>
  `;
};

const openPrescriptionWindow = (patient, medicines, clinical, remarks, instructions, autoPrint) => {
  const win = window.open("", "_blank", "width=850,height=1000");
  win.document.write(buildPrescriptionHTML(patient, medicines, clinical, remarks, instructions));
  win.document.close();
  win.focus();
  if (autoPrint) win.print();
};

const printPrescription = (patient, medicines, clinical, remarks, instructions) =>
  openPrescriptionWindow(patient, medicines, clinical, remarks, instructions, true);

const viewPrescription = (patient, medicines, clinical, remarks, instructions) =>
  openPrescriptionWindow(patient, medicines, clinical, remarks, instructions, false);

const PatientRecordsExport = ({ token }) => {
  const [patients, setPatients] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState("full");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [patientQuery, setPatientQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientResults, setPatientResults] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/patients`, authHeader(token)),
      axios.get(`${API}/consultations`, authHeader(token)),
    ]).then(([pRes, cRes]) => {
      setPatients(pRes.data);
      setConsultations(cRes.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [token]);

  useEffect(() => {
    if (!patientQuery.trim()) { setPatientResults([]); return; }
    const q = patientQuery.trim().toLowerCase();
    setPatientResults(patients.filter((p) =>
      p.name.toLowerCase().split(/\s+/).some((word) => word.length > 1 && word.startsWith(q)) || p.mobile.includes(q) || String(p.patient_id).includes(q)
    ).sort((a, b) => {
      const aFirst = a.name.toLowerCase().split(/\s+/)[0].startsWith(q) ? 0 : 1;
      const bFirst = b.name.toLowerCase().split(/\s+/)[0].startsWith(q) ? 0 : 1;
      return aFirst !== bFirst ? aFirst - bFirst : a.name.localeCompare(b.name);
    }).slice(0, 40));
  }, [patientQuery, patients]);

  const patientMap = useMemo(() => {
    const m = {};
    patients.forEach((p) => { m[p.patient_id] = p; });
    return m;
  }, [patients]);

  const formatMedicines = (meds) => (meds || []).map((m) =>
    `${m.name}${m.composition ? " (" + m.composition + ")" : ""} ${m.strength} (${m.intake}, B-L-D ${m.morning}-${m.afternoon}-${m.night}, ${m.duration})${m.remark ? " [" + m.remark + "]" : ""}`
  ).join("; ");

  const buildPrescriptionRows = (list) => list.map((c) => {
    const p = patientMap[c.patient_id] || {};
    return {
      "Patient ID": c.patient_id,
      "Patient Name": p.name || "",
      "Mobile": p.mobile || "",
      "Date": c.date,
      "Time": c.created_at ? new Date(c.created_at).toLocaleTimeString() : "",
      "Diagnosis": c.diagnosis || "",
      "Medicines": formatMedicines(c.medicines),
      "Clinical Notes": c.clinical_notes || "",
      "Investigations": c.investigations || "",
      "Remarks": c.additional_remarks || "",
      "Instructions": c.instructions || "",
    };
  });

  const buildPatientRows = (list) => list.map((p) => ({
    "Patient ID": p.patient_id,
    "Name": p.name,
    "Mobile": p.mobile,
    "Age": p.age,
    "Sex": p.sex,
    "Address": p.address,
    "Referred By": p.referred_by,
    "Registered Date": p.date,
    "Follow Up": p.follow_up,
    "Pulse": p.pulse,
    "BP": p.blood_pressure,
    "Height": p.height,
    "Weight": p.weight,
    "BMI": p.bmi,
  }));

  const download = () => {
    const wb = XLSX.utils.book_new();

    if (mode === "full") {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildPatientRows(patients)), "Patients");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildPrescriptionRows(consultations)), "Prescriptions");
      XLSX.writeFile(wb, `All_Patient_Records_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } else if (mode === "date") {
      if (!fromDate || !toDate) { toast.error("Pick both a from and to date"); return; }
      const filtered = consultations.filter((c) => c.date >= fromDate && c.date <= toDate);
      if (filtered.length === 0) { toast.error("No prescriptions in that date range"); return; }
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildPrescriptionRows(filtered)), "Prescriptions");
      XLSX.writeFile(wb, `Prescriptions_${fromDate}_to_${toDate}.xlsx`);
    } else if (mode === "patient") {
      if (!selectedPatient) { toast.error("Select a patient first"); return; }
      const filtered = consultations.filter((c) => c.patient_id === selectedPatient.patient_id);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildPatientRows([selectedPatient])), "Patient Info");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildPrescriptionRows(filtered)), "Prescriptions");
      XLSX.writeFile(wb, `${selectedPatient.name.replace(/\s+/g, "_")}_Records.xlsx`);
    }
  };

  return (
    <Card title="Patient Records Export">
      {!loaded && <p className="text-white/40 text-sm">Loading records...</p>}
      {loaded && (
        <>
          <div className="flex flex-wrap gap-2 mb-5">
            {[
              { id: "full", label: "Full" },
              { id: "date", label: "By Date" },
              { id: "patient", label: "By Patient" },
            ].map((m) => (
              <button key={m.id} type="button" onClick={() => setMode(m.id)}
                data-testid={`records-mode-${m.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  mode === m.id ? "bg-[#5ECCA0] text-[#14263D]" : "bg-[#14263D] text-white/60 border border-white/10"
                }`}>
                {m.label}
              </button>
            ))}
          </div>

          <div className="text-white/50 text-sm mb-4">
            {patients.length} patients on record · {consultations.length} prescriptions total
          </div>

          {mode === "date" && (
            <div className="grid sm:grid-cols-2 gap-5 mb-5 max-w-md">
              <div>
                <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                  className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="records-from-date-input" />
              </div>
              <div>
                <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                  className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="records-to-date-input" />
              </div>
            </div>
          )}

          {mode === "patient" && (
            <div className="relative mb-5 max-w-md">
              <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Patient</Label>
              <Input value={selectedPatient ? `${selectedPatient.name} (#${selectedPatient.patient_id})` : patientQuery}
                onFocus={() => {
                  setSelectedPatient(null);
                  const q = patientQuery.trim().toLowerCase();
                  const sorted = [...patients].sort((a, b) => a.name.localeCompare(b.name));
                  const rank = (p) => {
                    const aFirst = p.name.toLowerCase().split(/\s+/)[0].startsWith(q) ? 0 : 1;
                    return aFirst;
                  };
                  setPatientResults(
                    q
                      ? sorted
                          .filter((p) => p.name.toLowerCase().split(/\s+/).some((word) => word.length > 1 && word.startsWith(q)) || p.mobile.includes(q) || String(p.patient_id).includes(q))
                          .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
                          .slice(0, 40)
                      : sorted.slice(0, 40)
                  );
                }}
                onChange={(e) => { setSelectedPatient(null); setPatientQuery(e.target.value); }}
                placeholder="Search by name, mobile, or ID"
                className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="records-patient-search-input" />
              {!selectedPatient && patientResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-[#14263D] border border-white/15 rounded-lg max-h-64 overflow-y-auto">
                  {patientResults.map((p) => (
                    <button key={p.patient_id} type="button" onClick={() => { setSelectedPatient(p); setPatientResults([]); }}
                      className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10">
                      {p.name} <span className="text-white/40">#{p.patient_id} · {p.mobile}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={download} data-testid="records-download-btn"
              className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 flex items-center gap-2">
              <Download size={16} /> Download Excel
            </button>
            {mode === "patient" && selectedPatient && (
              <button type="button" onClick={() => {
                const patientCons = consultations
                  .filter((c) => c.patient_id === selectedPatient.patient_id)
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                if (patientCons.length === 0) { toast.error("No prescriptions found for this patient"); return; }
                const last = patientCons[0];
                viewPrescription(selectedPatient, last.medicines || [], {
                  diagnosis: last.diagnosis, dm: last.dm, htn: last.htn, copd: last.copd,
                  antiplatelets: last.antiplatelets, thyroid: last.thyroid,
                  clinical_notes: last.clinical_notes, investigations: last.investigations, physiotherapy: last.physiotherapy,
                }, last.additional_remarks, last.instructions);
              }} data-testid="records-view-last-btn"
                className="rounded-full bg-white/10 text-white font-medium px-6 py-2.5 flex items-center gap-2 border border-white/15 hover:border-[#5ECCA0]/50">
                <Eye size={16} /> View Last Prescription
              </button>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

const PrescriptionPreview = ({ patient, clinical, medicineList, remarks, instructions }) => {
  const scrollRef = useRef(null);
  const onWheel = (e) => {
    if (scrollRef.current) scrollRef.current.scrollTop += e.deltaY;
  };
  const row = (label, value) => (
    <div key={label}><span className="text-[#777]">{label}</span><div className="font-medium">{value || "-"}</div></div>
  );

  return (
    <div ref={scrollRef} onWheel={onWheel} className="lg:sticky lg:top-6 bg-white text-[#14263D] rounded-2xl p-6 shadow-lg max-h-[85vh] overflow-y-auto overscroll-contain break-words" style={{ fontFamily: "Arial, sans-serif", overflowWrap: "break-word", wordBreak: "break-word" }}>
      <div className="flex justify-between items-start gap-4 border-b-2 border-[#3E7B5E] pb-3 mb-3">
        <div>
          <h1 className="text-lg font-bold m-0">Dr. Amit Kumar Chanduka</h1>
          <div className="text-[12px] text-[#14263D] mt-0.5">MS, M.Ch Neurosurgery (Gold Medalist)</div>
          <div className="text-[12px] text-[#14263D]">Brain &amp; Spine Surgeon &middot; Senior Consultant Neurosurgeon</div>
          <div className="text-[11px] mt-1">OCMR-15781/06</div>
        </div>
        <div className="text-right text-[10px] leading-snug max-w-[220px] flex-shrink-0">
          <div className="text-[#14263D]">Fellowship in Spine Surgery</div>
          <div className="text-[#666] mb-1">(Sir GangaRam Hospital, New Delhi, India)</div>
          <div className="text-[#14263D]">Fellowship in Endoscopic Spine Surgery</div>
          <div className="text-[#666] mb-1">(Seoul, South Korea)</div>
          <div className="text-[#14263D]">Fellowship in Endovascular Neurointervention</div>
          <div className="text-[#666] mb-1">(SMS Hospital, Jaipur, India)</div>
          <div className="text-[#14263D]">Fellowship in Stereotactic &amp; Functional Neurosurgery</div>
          <div className="text-[#666] mb-1">(Seoul, South Korea)</div>
          <div className="text-[#14263D]">Formerly at Manipal Hospital &amp; Sakra World Hospital, Bangalore</div>
        </div>
      </div>

      <div className="text-[15px] uppercase mb-1.5">
        <span className="text-[#777] normal-case">Patient:</span> <span className="font-bold">{patient.name}</span>{(patient.age || patient.sex) ? ` (${patient.age}/${patient.sex})` : ""}
      </div>
      <div className="grid grid-cols-2 gap-x-5 gap-y-1 text-[13px] uppercase mb-1">
        <div><span className="text-[#777] normal-case">Mobile:</span> {patient.mobile}</div>
        <div><span className="text-[#777] normal-case">Date:</span> {formatPrintDate(patient.date)}</div>
      </div>
      <div className="grid grid-cols-[2fr_1fr] gap-x-4 gap-y-1 text-[11px] uppercase mb-2">
        <div><span className="text-[#777] normal-case">Address:</span> {patient.address || "-"}</div>
        <div><span className="text-[#777] normal-case">Referred By:</span> {patient.referred_by || "-"}</div>
      </div>

      <div className="grid grid-cols-5 gap-2 text-[11px] border-t border-[#ddd] py-2">
        {row("Pulse", patient.pulse)}
        {row("BP", patient.blood_pressure)}
        {row("Height", patient.height)}
        {row("Weight", patient.weight)}
        {row("BMI", patient.bmi)}
      </div>

      <div className="grid grid-cols-5 gap-2 text-[11px] border-t border-[#ddd] py-2">
        {row("DM", clinical.dm)}
        {row("HTN", clinical.htn)}
        {row("COPD", clinical.copd)}
        {row("Antiplatelets", clinical.antiplatelets)}
        {row("Thyroid", clinical.thyroid)}
      </div>

      <div className="text-[11px] border-t border-b border-[#ddd] py-2 mb-3">
        <span className="text-[#777] uppercase text-[10px] mr-1">Diagnosis:</span>
        <span className="font-bold">{clinical.diagnosis || "-"}</span>
      </div>

      {(clinical.clinical_notes || clinical.investigations) && (
        <div className="grid grid-cols-2 gap-4 mb-2">
          <div>
            {clinical.clinical_notes && (
              <>
                <div className="text-[11px] font-bold text-[#14263D] uppercase underline decoration-1 underline-offset-2 mb-1">Clinical Notes</div>
                <ul className="list-disc ml-5 text-[12px]">
                  {clinical.clinical_notes.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </>
            )}
          </div>
          <div>
            {clinical.investigations && (
              <>
                <div className="text-[11px] font-bold text-[#14263D] uppercase underline decoration-1 underline-offset-2 mb-1">Investigations</div>
                <ul className="list-disc ml-5 text-[12px]">
                  {clinical.investigations.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      <table className="w-full text-[11px] mt-2 border-collapse">
        <thead>
          <tr className="bg-[#14263D] text-white text-[10px]">
            <th className="p-1.5 text-left">Sl.</th>
            <th className="p-1.5 text-left">Medicine Name</th>
            <th className="p-1.5 text-left">Mg</th>
            <th className="p-1.5 text-left">Intake</th>
            <th className="p-1.5 text-left">Remark</th>
            <th className="p-1.5 text-left">B</th>
            <th className="p-1.5 text-left">L</th>
            <th className="p-1.5 text-left">D</th>
            <th className="p-1.5 text-left">Duration</th>
          </tr>
        </thead>
        <tbody>
          {medicineList.map((m, i) => (
            <tr key={i} className="border-b border-[#eee]">
              <td className="p-1.5">{i + 1}</td>
              <td className="p-1.5">
                <span className="font-bold">{m.name}</span>
                {m.composition && <span className="block text-[9px] text-[#888]">{m.composition}</span>}
              </td>
              <td className="p-1.5">{m.strength}</td>
              <td className="p-1.5">{m.intake}</td>
              <td className="p-1.5">{m.remark}</td>
              <td className="p-1.5">{m.morning || "–"}</td>
              <td className="p-1.5">{m.afternoon || "–"}</td>
              <td className="p-1.5">{m.night || "–"}</td>
              <td className="p-1.5">{m.duration}</td>
            </tr>
          ))}
          {medicineList.length === 0 && (
            <tr><td colSpan={9} className="p-2 text-[#999]">No medicines added yet</td></tr>
          )}
        </tbody>
      </table>

      {clinical.physiotherapy && (
        <>
          <div className="text-[11px] font-bold text-[#14263D] uppercase underline decoration-1 underline-offset-2 mt-2 mb-1">Physiotherapy</div>
          <ul className="list-disc ml-5 text-[12px]">
            {clinical.physiotherapy.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </>
      )}
      {remarks && remarks.split("\n").map((l) => l.trim()).filter(Boolean).length > 0 && (
        <div className="text-[12px] mt-2 mb-1">
          <b className="underline decoration-1 underline-offset-2">Remarks:</b>
          <ul className="list-disc ml-5 mt-1">
            {remarks.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
      )}
      {instructions && instructions.split("\n").map((l) => l.trim()).filter(Boolean).length > 0 && (
        <div className="text-[12px] mb-1">
          <b className="underline decoration-1 underline-offset-2">Instructions:</b>
          <ul className="list-disc ml-5 mt-1">
            {instructions.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-5 border border-[#14263D] p-3 text-[10px] leading-relaxed">
        <b>Follow Up:</b> ___________________&nbsp;&nbsp;&nbsp;
        <b>Appointments:</b> 9040965647 / 9583021000 / 9938122302<br />
        <b>Life &amp; Mind Clinic:</b> Plot no. 1428, Unit-17, Sastrinagar, Nayapalli, Bhubaneswar<br />
        <b>KIMS Superspeciality Hospital:</b> Kiit Campus-5, Kiit University, Patia, Bhubaneswar&nbsp;&nbsp;&nbsp;
        <b>Tel:</b> +91 674 6661016 / 6661066
      </div>
    </div>
  );
};

const SaveConfirmModal = ({ open, onSave, onEdit }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onEdit}>
      <div className="bg-[#1B3352] border border-white/10 rounded-2xl p-8 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-white text-lg font-medium mb-2">Save this prescription?</h2>
        <p className="text-white/50 text-sm mb-6">This will save the consultation and print the prescription.</p>
        <div className="flex gap-3">
          <button onClick={onSave} data-testid="clinic-confirm-save-btn"
            className="flex-1 rounded-full bg-[#5ECCA0] text-[#14263D] font-medium py-2.5">
            Save
          </button>
          <button onClick={onEdit} data-testid="clinic-confirm-edit-btn"
            className="flex-1 rounded-full bg-white/10 text-white font-medium py-2.5 border border-white/15">
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { id: "prescribe", label: "Prescribe" },
  { id: "records", label: "Patient Records" },
];

const Clinic = () => {
  const [token, setToken] = useState(localStorage.getItem("clinic_token") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("prescribe");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [medicineList, setMedicineList] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [instructions, setInstructions] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [dm, setDm] = useState("");
  const [htn, setHtn] = useState("");
  const [copd, setCopd] = useState("");
  const [antiplatelets, setAntiplatelets] = useState("");
  const [thyroid, setThyroid] = useState("");
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [investigations, setInvestigations] = useState("");
  const [physiotherapy, setPhysiotherapy] = useState("");
  const [saving, setSaving] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/clinic/login`, { password });
      setToken(res.data.token);
      localStorage.setItem("clinic_token", res.data.token);
      toast.success("Logged in");
    } catch (err) {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("clinic_token");
    setToken("");
  };

  const resetVisitFields = () => {
    setMedicineList([]);
    setEditingMedicineIndex(null);
    setRemarks("");
    setInstructions("");
    setDiagnosis("");
    setDm("");
    setHtn("");
    setCopd("");
    setAntiplatelets("");
    setThyroid("");
    setClinicalNotes("");
    setInvestigations("");
    setPhysiotherapy("");
  };

  const selectPatient = (p) => {
    setSelectedPatient(p);
    resetVisitFields();
  };

  const [editingMedicineIndex, setEditingMedicineIndex] = useState(null);

  const addMedicine = (entry) => setMedicineList((list) => [...list, entry]);
  const updateMedicine = (index, entry) => setMedicineList((list) => list.map((m, i) => (i === index ? entry : m)));
  const removeMedicine = (i) => {
    setMedicineList((list) => list.filter((_, idx) => idx !== i));
    setEditingMedicineIndex((cur) => {
      if (cur === null) return null;
      if (cur === i) return null;
      if (cur > i) return cur - 1;
      return cur;
    });
  };
  const startEditMedicine = (i) => setEditingMedicineIndex(i);
  const cancelEditMedicine = () => setEditingMedicineIndex(null);
  const saveMedicine = (entry) => {
    if (editingMedicineIndex !== null) {
      updateMedicine(editingMedicineIndex, entry);
      setEditingMedicineIndex(null);
    } else {
      addMedicine(entry);
    }
  };

  const clinicalData = { diagnosis, dm, htn, copd, antiplatelets, thyroid, clinical_notes: clinicalNotes, investigations, physiotherapy };

  const saveAndPrint = async () => {
    if (!selectedPatient) return;
    setShowSaveConfirm(false);
    setSaving(true);
    try {
      await axios.post(`${API}/consultations`, {
        patient_id: selectedPatient.patient_id,
        date: new Date().toISOString().slice(0, 10),
        medicines: medicineList,
        additional_remarks: remarks,
        instructions,
        ...clinicalData,
      }, authHeader(token));
      toast.success("Consultation saved");
      setHistoryKey((k) => k + 1);
    } catch (err) {
      toast.error("Not saved to records — please re-save once the connection is back", { duration: 8000 });
    } finally {
      setSaving(false);
    }
    printPrescription(selectedPatient, medicineList, clinicalData, remarks, instructions);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#14263D] flex items-center justify-center p-6">
        <form onSubmit={login} className="bg-[#1B3352] border border-white/10 rounded-2xl p-8 w-full max-w-sm">
          <h1 className="text-white text-xl font-medium mb-6">Clinic Login</h1>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Password</Label>
          <div className="relative mt-2 mb-5">
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#14263D] border-white/15 text-white pr-10"
              data-testid="clinic-password-input"
            />
            <button type="button" onClick={() => setShowPassword((v) => !v)} tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
              data-testid="clinic-password-toggle-btn">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="clinic-login-btn"
            className="w-full rounded-full bg-[#5ECCA0] text-[#14263D] font-medium py-3 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Log In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#14263D] p-6 md:p-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-white text-2xl font-medium">Clinic Dashboard</h1>
          <button onClick={logout} data-testid="clinic-logout-btn" className="text-white/50 hover:text-white text-sm">
            Log out
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`clinic-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === t.id ? "bg-[#5ECCA0] text-[#14263D]" : "bg-[#1B3352] text-white/60 hover:text-white border border-white/10"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "prescribe" && (
          <>
            {!selectedPatient && (
              <>
                <TodaysPatients token={token} refreshKey={historyKey} onSelect={selectPatient} />
                <PatientSearch token={token} onSelect={selectPatient} />
              </>
            )}

            {selectedPatient && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div>
                  <PatientDetailsPanel patient={selectedPatient} onClear={() => setSelectedPatient(null)} />
                  <ClinicalAssessmentCard
                    diagnosis={diagnosis} setDiagnosis={setDiagnosis}
                    dm={dm} setDm={setDm} htn={htn} setHtn={setHtn} copd={copd} setCopd={setCopd}
                    antiplatelets={antiplatelets} setAntiplatelets={setAntiplatelets}
                    thyroid={thyroid} setThyroid={setThyroid}
                  />
                  <ClinicalNotesInvestigationCard
                    clinicalNotes={clinicalNotes} setClinicalNotes={setClinicalNotes}
                    investigations={investigations} setInvestigations={setInvestigations}
                  />
                  <MedicineForm token={token} editingEntry={editingMedicineIndex !== null ? medicineList[editingMedicineIndex] : null} onSave={saveMedicine} onCancelEdit={cancelEditMedicine} />
                  <MedicineListTable items={medicineList} onRemove={removeMedicine} onEdit={startEditMedicine} />
                  <PhysiotherapyCard physiotherapy={physiotherapy} setPhysiotherapy={setPhysiotherapy} />

                  <Card title="Remarks & Instructions">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Additional Remarks</Label>
                        <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} spellCheck="true"
                          className="mt-2 w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-3 text-sm"
                          data-testid="clinic-remarks-input" />
                        <SpellSuggestions text={remarks} onReplace={setRemarks} />
                      </div>
                      <div>
                        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Instructions</Label>
                        <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={4} spellCheck="true"
                          className="mt-2 w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-3 text-sm"
                          data-testid="clinic-instructions-input" />
                        <SpellSuggestions text={instructions} onReplace={setInstructions} />
                      </div>
                    </div>
                  </Card>

                  <div className="flex flex-wrap items-center gap-3 mb-8">
                    <button onClick={() => setShowSaveConfirm(true)} disabled={saving} data-testid="clinic-save-print-btn"
                      className="rounded-full bg-purple-500 text-white font-medium px-8 py-2.5 disabled:opacity-60 flex items-center gap-2 text-base">
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <><Printer size={16} /> Save &amp; Print</>}
                    </button>
                  </div>
                  <SaveConfirmModal open={showSaveConfirm} onSave={saveAndPrint} onEdit={() => setShowSaveConfirm(false)} />

                  <ConsultationHistory key={historyKey} token={token} patientId={selectedPatient.patient_id} patient={selectedPatient} />
                </div>

                <PrescriptionPreview patient={selectedPatient} clinical={clinicalData} medicineList={medicineList} remarks={remarks} instructions={instructions} />
              </div>
            )}
          </>
        )}

        {activeTab === "records" && <PatientRecordsExport token={token} />}
      </div>
    </div>
  );
};

export default Clinic;
